import Image from '@/components/Image'
import Link from '@/components/Link'
import rteSections from '@/components/sections'
import { Course, Glossary, Syllabus } from '@/kontent/content-types'
import { contentTypes } from '@/kontent/project/contentTypes'
import { TaxoKeyLearningArea } from '@/kontent/taxonomies'
import type {
	Mapping,
	TaxoStageWithLifeSkill,
	TaxoStageYearWithLifeSkill,
} from '@/types'
import { FetchLinksFromAzureResponseData } from '@/types/fetchLinksFromAzure'
import type {
	ElementModels,
	Elements,
	IContentItemsContainer,
	ILink,
	IRichTextImage,
	Responses,
} from '@kontent-ai/delivery-sdk'
import clsx from 'clsx'
import { Dispatch, SetStateAction, useMemo } from 'react'
import RichTextComponent, { RichTextComponentProps } from './RichTextComponent'
import { RichtextLinkWrapper } from './richtextlinks/RichtextLinkWrapper'

export interface RichTextProps {
	className?: string
	richTextElement: Elements.RichTextElement
	linkedItems: IContentItemsContainer
	mappings: Mapping[]

	/** To create unique id for heading */
	suffixForHeading?: string

	renderFnBefore?: RichTextComponentProps['renderFnBefore']

	/**
	 * For focus area purpose (e.g. teaching advice page)
	 */

	currentPath?: string
	currentSyllabus?: Syllabus
	currentKeyLearningAreas?: ElementModels.TaxonomyTerm<TaxoKeyLearningArea>[]
	currentStage?: TaxoStageWithLifeSkill
	currentYear?: TaxoStageYearWithLifeSkill
	isLifeSkillMode?: boolean
	rootGlossaryPopoverData?:
		| Responses.IViewContentItemResponse<Glossary>
		| FetchLinksFromAzureResponseData
	setGlossaryPopoverParentData?: Dispatch<
		SetStateAction<this['rootGlossaryPopoverData']>
	>
	resolveFootnotesLink?: RichTextComponentProps['resolveLink']

	/**
	 * Disable copy-url on headings
	 */
	disableCopyUrl?: boolean

	/**
	 * Copy URL prefix
	 */
	copyUrlPrefix?: string
	resolveCourseItems?: Course[]

	/**
	 * Query parameters to inject into specific links by codename
	 * Array of tuples: [linkCodename, queryString]
	 */
	linkQueryParams?: [string, string][]

	pageType?: string
}

type ImageOrientation = 'square' | 'landscape' | 'portrait'

const getImageOrientation = (
	width: number,
	height: number,
): ImageOrientation => {
	const ratio = width / height
	const min = 7 / 10
	const max = 1 / min
	if (ratio < min) return 'portrait'
	if (ratio > max) return 'landscape'
	return 'square'
}

function RichText(props: RichTextProps) {
	const {
		richTextElement,
		linkedItems,
		mappings,
		currentPath,
		suffixForHeading,
		currentSyllabus,
		currentStage,
		currentYear,
		currentKeyLearningAreas,
		isLifeSkillMode = false,
		rootGlossaryPopoverData,
		setGlossaryPopoverParentData,
		resolveFootnotesLink,
		renderFnBefore,
		disableCopyUrl = false,
		copyUrlPrefix,
		resolveCourseItems,
		linkQueryParams,
		pageType,
		...rest
	} = props

	const _copyUrlPrefix = useMemo(() => {
		if (copyUrlPrefix) return copyUrlPrefix
		if (currentPath) {
			const url = new URL(currentPath, 'https://curriculum.nsw.edu.au')
			return url.pathname + url.search
		}
	}, [copyUrlPrefix, currentPath])

	return (
		<RichTextComponent
			{...rest}
			className={clsx('richtext', props.className)}
			richTextElement={richTextElement}
			linkedItems={linkedItems}
			mappings={mappings}
			resolveLinkedItem={(linkedItem, domNode, domToReact) => {
				if (!linkedItem) return domToReact([domNode])
				const RichtextSectionComponent =
					rteSections[
						linkedItem.system.type == 'content_course'
							? 'contentrichtext'
							: linkedItem.system.type
					]

				if (RichtextSectionComponent) {
					return (
						<RichtextSectionComponent
							currentPath={currentPath}
							currentStage={currentStage}
							currentYear={currentYear}
							currentSyllabus={currentSyllabus}
							currentKeyLearningAreas={currentKeyLearningAreas}
							isLifeSkillMode={isLifeSkillMode}
							data-kontent-item-id={linkedItem.system.id}
							linkedItem={linkedItem}
							mappings={mappings}
							linkedItems={linkedItems}
							resolveFootnotesLink={resolveFootnotesLink}
							pageType={pageType}
						/>
					)
				}
				return domToReact([domNode])
			}}
			resolveImage={(image: IRichTextImage, _domNode, _domToReact) => {
				return (
					<div
						className={clsx(
							'RichText__img-wrapper',
							`RichText__img-wrapper--${getImageOrientation(
								image.width,
								image.height,
							)}`,
						)}
					>
						<Image
							width={image.width}
							asset={image}
							alt={image.description}
						/>
					</div>
				)
			}}
			resolveLink={(link: ILink, mappings, domNode, domToReact) => {
				if (link?.type === contentTypes.ace_footnote.codename) {
					if (resolveFootnotesLink) {
						return resolveFootnotesLink(
							link,
							mappings,
							domNode,
							domToReact,
						)
					}
				}

				return (
					<RichtextLinkWrapper
						link={link}
						linkedItems={linkedItems}
						mappings={mappings}
						currentSyllabus={currentSyllabus}
						currentStage={currentStage}
						currentYear={currentYear}
						rootGlossaryPopoverData={rootGlossaryPopoverData}
						setGlossaryPopoverParentData={
							setGlossaryPopoverParentData
						}
					>
						{(linkProps) => {
							const isLinkResourceFile =
								link?.type ===
								contentTypes.link_resource_file.codename

							const isLinkGlossary =
								link?.type ===
								contentTypes.link_glossary.codename
							const isLinkWebLinkVideo =
								link?.type ===
								contentTypes.weblinkvideo.codename

							if (isLinkGlossary) {
								return domToReact(domNode.children)
							}
							if (linkProps.href) {
								// Inject query parameters if provided
								let finalHref = linkProps.href as string

								// Check for link-specific query parameters first
								if (
									linkQueryParams &&
									linkQueryParams.length > 0
								) {
									const matchingParams = linkQueryParams.find(
										([codename]) =>
											link?.codename === codename,
									)
									if (matchingParams) {
										const [, queryString] = matchingParams
										try {
											const url = new URL(
												finalHref,
												window.location.origin,
											)
											// Append the specific query string
											const separator = url.search
												? '&'
												: '?'
											finalHref =
												url.pathname +
												url.search +
												separator +
												queryString
										} catch (error) {
											// If URL construction fails, append directly
											const separator =
												finalHref.includes('?')
													? '&'
													: '?'
											finalHref =
												finalHref +
												separator +
												queryString
										}
									}
								}

								if (isLinkWebLinkVideo) {
									return (
										//@ts-ignore
										<a {...linkProps} href={finalHref}>
											{domToReact(domNode.children)}
										</a>
									)
								}

								if (
									isLinkResourceFile ||
									linkProps.target === '_blank' ||
									finalHref.includes('#')
								) {
									return (
										<a
											//@ts-ignore
											{...linkProps}
											href={linkProps.href as string}
											target={linkProps.target}
											download={
												isLinkResourceFile || undefined
											}
										>
											{domToReact(domNode.children)}
										</a>
									)
								}
								return (
									<Link {...linkProps} href={finalHref}>
										{domToReact(domNode.children)}
									</Link>
								)
							}
							return <del>{domToReact([domNode])}</del>
						}}
					</RichtextLinkWrapper>
				)
			}}
			resolveDomNode={(domNode) => {
				return domNode
			}}
			suffixForHeading={suffixForHeading}
			renderFnBefore={renderFnBefore}
			disableCopyUrl={disableCopyUrl}
			copyUrlPrefix={_copyUrlPrefix}
			resolveCourseItems={resolveCourseItems}
		></RichTextComponent>
	)
}

export default RichText
